import { useEffect, useRef, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { isNativeApp } from "@/lib/native-app";
import { hapticSelection } from "@/lib/native-runtime";

const THRESHOLD = 78;

/**
 * Pull-to-refresh for the native app only. Reloads the current screen's data
 * without touching how the website behaves in a browser.
 */
export function NativePullToRefresh() {
  const router = useRouter();
  const [offset, setOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);

  useEffect(() => {
    if (!isNativeApp()) return;

    const onStart = (e: TouchEvent) => {
      if (window.scrollY > 0) return;
      startY.current = e.touches[0]?.clientY ?? null;
    };
    const onMove = (e: TouchEvent) => {
      if (startY.current == null || refreshing) return;
      const dy = (e.touches[0]?.clientY ?? 0) - startY.current;
      if (dy <= 0 || window.scrollY > 0) {
        setOffset(0);
        return;
      }
      setOffset(Math.min(dy * 0.5, THRESHOLD + 20));
    };
    const onEnd = async () => {
      const pulled = offset;
      startY.current = null;
      if (pulled >= THRESHOLD) {
        setRefreshing(true);
        void hapticSelection();
        try {
          await router.invalidate();
        } finally {
          setRefreshing(false);
        }
      }
      setOffset(0);
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
    };
  }, [offset, refreshing, router]);

  if (offset === 0 && !refreshing) return null;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-[env(safe-area-inset-top)] z-[90] flex justify-center"
      style={{ transform: `translateY(${refreshing ? THRESHOLD / 2 : offset / 2}px)` }}
    >
      <span className="rounded-full bg-background/95 px-3 py-1 text-xs text-muted-foreground shadow">
        {refreshing ? "Refreshing…" : offset >= THRESHOLD ? "Release to refresh" : "Pull to refresh"}
      </span>
    </div>
  );
}
